import React, { useMemo, useState } from 'react';
import DateField from '../connectedForm/DateInput/DateInput';
import { ReportTemplate } from '../../graphql/entities';
import GenerateVatAuditReportMutation from '../../graphql/mutations/vat/GenerateVatAuditReportMutation.graphql';
import {
  FilterOperation,
  IGenerateVatAuditReportMutationResult,
  IGenerateVatAuditReportMutationVariables,
  IReportTemplateEdge,
  IReportTemplatesDropdownQueryResult,
  IReportTemplatesDropdownQueryVariables,
  ReportOutputType,
} from '../../graphql/schema/p4types';
import { useQuery } from '@apollo/client';
import { Maybe } from 'graphql/jsutils/Maybe';
import Dropdown from '../connectedForm/Dropdown';
import { getDefaultOutputType } from '../../utils/getDefaultOutputType';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import { useCheckPopUpBlockerFunction } from '../../utils/CheckPopUpBlockerFunction';
import { BasicHeader } from '../../actions/BasicHeader';
import getClientMutationId from '../../utils/getClientMutationId';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { ModalBottomButtonContainer } from './Styles';
import { Button, SuccessButton } from '../connectedForm/Button';
import FluentModal from './FluentModal';

const VatAuditReportOutputSelection = [
  {
    value: ReportOutputType.Email,
    label: 'E-Mail',
  },
  {
    value: ReportOutputType.LocalPrinter,
    label: 'Local Printer',
  },
  {
    value: ReportOutputType.SystemPrinter,
    label: 'System Printer',
  },
  {
    value: ReportOutputType.Xlsx,
    label: 'XLSX Spreadsheet',
  },
];

const VatAuditReportCriteriaModal = ({ onClose, isOpen }: { onClose: () => void; isOpen: boolean }) => {
  const [template, setTemplate] = useState<Maybe<IReportTemplateEdge>>();
  const [outputType, setOutputType] = useState<Maybe<{ value: ReportOutputType; label: string }>>();
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();

  const { data: templateData } = useQuery<IReportTemplatesDropdownQueryResult, IReportTemplatesDropdownQueryVariables>(
    ReportTemplate.dropdown.query,
    {
      variables: {
        filters: [
          {
            filterParameters: {
              field: 'reportType',
              value: 'VAT_AUDIT_REPORT',
            },
            filterOperation: FilterOperation.Equals,
          },
          {
            filterParameters: {
              field: 'reportType',
              value: 'VAT_AUDIT_TRANSACTION_REPORT',
            },
            filterOperation: FilterOperation.Equals,
          },
        ],
      },
    }
  );
  const templates = useMemo(() => {
    return templateData?.list?.edges ?? [];
  }, [templateData?.list?.edges]);

  const checkPopUpBlockerFunction = useCheckPopUpBlockerFunction();
  const [generateVatAuditReport] = useBackgroundMutation<
    IGenerateVatAuditReportMutationResult,
    IGenerateVatAuditReportMutationVariables
  >(GenerateVatAuditReportMutation, {
    onCompleteJob: (job) => {
      if (job.properties.reportFile) {
        const newTab = checkPopUpBlockerFunction(`/api/resources/report/${job.properties.reportFile}`, '_blank');
        newTab?.print();
      }
      onClose();
    },
  });

  const onPrint = () => {
    generateVatAuditReport({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          reportParameters: {
            template: normalizeVariables(template?.node ?? null),
            outputType: outputType?.value ?? null,
          },
          vatAuditReportCriteria: normalizeVariables({
            startDate: startDate,
            endDate: endDate,
            uuid: getClientMutationId(),
          }),
        },
      },
    });
  };

  return (
    <FluentModal isOpen={isOpen} styles={{ main: { minWidth: '450px', paddingBottom: '60px' } }}>
      <BasicHeader>Vat Audit Report</BasicHeader>
      <div style={{ padding: '1em' }}>
        <Dropdown
          options={templates}
          valueKey="node.id"
          labelKey="node.name"
          onChange={(e: any) => {
            setTemplate(e);
            setOutputType(getDefaultOutputType(e?.node?.reportTemplateFormat));
          }}
          label="Template"
          value={template}
        />
        <Dropdown
          dataid="outputType"
          options={VatAuditReportOutputSelection}
          label="Output"
          onChange={setOutputType}
          value={outputType}
          valueKey="value"
          labelKey="label"
          clearable={false}
        />
        <DateField
          data-testid={'vat-audit-start-date'}
          label="Start Date"
          portal={true}
          value={startDate}
          onChange={setStartDate}
        />
        <DateField
          data-testid={'vat-audit-end-date'}
          label="End Date"
          portal={true}
          value={endDate}
          onChange={setEndDate}
        />
      </div>
      <ModalBottomButtonContainer>
        <Button data-testid="vat-audit-report-cancel" onClick={onClose}>
          Cancel
        </Button>
        <SuccessButton data-testid="vat-audit-report-print" onClick={onPrint}>
          Print
        </SuccessButton>
      </ModalBottomButtonContainer>
    </FluentModal>
  );
};

export default VatAuditReportCriteriaModal;
